import { CardOptionContent } from '@framework/form/inputs/card-group/CardOptionContent.tsx';
import { type IRadioCardOption } from '@framework/form/inputs/card-group/RadioCardGroupInput.tsx';
import { TbIcon } from '@framework/ui/components/icon/TbIcon.tsx';
import { yup } from '@framework/validation/initYup.ts';
import { IconListCheck, IconSelect } from '@tabler/icons-react';
import { useWatch } from 'react-hook-form';
import { type IWidgetComponentProps } from '../../../IWidgetType.ts';
import { WidgetType } from '../../../Widget.ts';
import { BaseLayoutSchema } from '../../common/BaseLayoutSchema.ts';
import { WidgetConfigurationSlotRender } from '../../common/config-slots/WidgetConfigurationSlotRender.tsx';
import { WidgetConfigurationSlots } from '../../common/config-slots/WidgetConfigurationSlots.tsx';
import {
  CommonConfigComponent,
  CommonInputWidgetConfigSchema,
} from '../common/CommonInputConfigComponent.tsx';
import { CommonKindInputConfig } from '../common/CommonKindInputConfig.tsx';
import {
  HideLabelConfigInput,
  hideLabelConfigSchema,
} from '../common/HideLabelConfigInput.tsx';
import { LabelLayoutConfigInput } from '../common/label-layout/LabelLayoutConfigInput.tsx';
import {
  CommonOptionListConfigComponent,
  commonOptionListDirectionValidator,
  commonOptionListSeparatorValidator,
} from '../common/option-list/CommonOptionListConfigComponent.tsx';
import { SetOptionsFromVariableInput } from '../common/option-list/SetOptionsFromVariableInput.tsx';
import { widgetOptionArrayValidator } from '../common/option-list/validation/optionArrayValidator.ts';
import { WidgetOptionItemList } from '../common/option-list/WidgetOptionItemList.tsx';
import {
  type IMultiSelectWidget,
  MultiSelectKind,
} from './IMultiSelectWidget.ts';

export const multiSelectInputConfigValidator: yup.ObjectSchema<IMultiSelectWidget> =
  CommonInputWidgetConfigSchema.shape({
    type: yup.string().exact(WidgetType.MultiSelect).required(),
    options: widgetOptionArrayValidator,
    kind: yup
      .string()
      .oneOfEnum(MultiSelectKind)
      .default(MultiSelectKind.Checkbox)
      .required()
      .label('Tipo di campo'),
    layout: BaseLayoutSchema.shape({
      hideLabel: hideLabelConfigSchema,
    })
      // Opzioni solo per checkbox
      .when('kind', ([kind]: MultiSelectKind[], schema) => {
        if (kind === MultiSelectKind.Checkbox) {
          // return schema.shape(commonOptionListConfigValidator);
          return schema.shape({
            direction: commonOptionListDirectionValidator,
            separator: commonOptionListSeparatorValidator,
          });
        }
        return schema;
      })
      .required()
      .noUnknown(),
  })
    .required()
    .noUnknown();

const kindOptions: IRadioCardOption[] = [
  {
    value: MultiSelectKind.Select,
    label: (
      <CardOptionContent
        icon={<TbIcon icon={IconSelect} />}
        title="Menù a tendina"
        description="Apre un elenco a comparsa con selezione multipla."
      />
    ),
  },
  {
    value: MultiSelectKind.Checkbox,
    label: (
      <CardOptionContent
        icon={<TbIcon icon={IconListCheck} />}
        title="Lista di spunte"
        description="Tutte le opzioni sempre visibili, con caselle di spunta."
      />
    ),
  },
];

export interface IMultiSelectConfigComponentProps
  extends IWidgetComponentProps<IMultiSelectWidget> {}

export function MultiSelectConfigComponent(
  props: IMultiSelectConfigComponentProps,
) {
  // useWatch (non getValue): il valore decide la registrazione dei fill,
  // serve la sottoscrizione al campo per aggiornare le card al cambio.
  const kind = useWatch({ name: 'kind' }) as MultiSelectKind;

  return (
    <>
      <CommonKindInputConfig kindOptions={kindOptions} />

      {/* Configurazioni comuni */}
      <CommonConfigComponent
        {...props}
        hideConfigPlaceholder={kind === MultiSelectKind.Checkbox}
        noConfirmation={true}
      />

      {/* Disposizione etichetta, orientamento e separatori: solo spunte */}
      {kind === MultiSelectKind.Checkbox && (
        <WidgetConfigurationSlotRender
          slot={WidgetConfigurationSlots.Layout}
          order={20}
        >
          <LabelLayoutConfigInput />
          <CommonOptionListConfigComponent />
        </WidgetConfigurationSlotRender>
      )}
      <WidgetConfigurationSlotRender
        slot={WidgetConfigurationSlots.Layout}
        order={30}
      >
        <HideLabelConfigInput />
      </WidgetConfigurationSlotRender>
      <WidgetConfigurationSlotRender slot={WidgetConfigurationSlots.Options}>
        <WidgetOptionItemList name={'options'} />
        <SetOptionsFromVariableInput name="options" />
      </WidgetConfigurationSlotRender>
    </>
  );
}
